import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataRefreshHistories = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');

  const refreshHistories = [
    {
      id: 1,
      system: 'SAP ERP',
      status: 'success',
      startTime: '2026-02-10T02:30:00',
      endTime: '2026-02-10T02:32:15',
      recordsProcessed: 45678,
      duration: 135,
      triggeredBy: 'Automated Schedule',
      details: 'Successfully synchronized financial and operational data including revenue, cost, and resource allocation records',
    },
    {
      id: 2,
      system: 'Salesforce CRM',
      status: 'success',
      startTime: '2026-02-10T02:28:00',
      endTime: '2026-02-10T02:29:45',
      recordsProcessed: 12340,
      duration: 105,
      triggeredBy: 'Automated Schedule',
      details: 'Customer data and sales pipeline information updated successfully with latest opportunity and account data',
    },
    {
      id: 3,
      system: 'Workday HCM',
      status: 'success',
      startTime: '2026-02-10T02:25:00',
      endTime: '2026-02-10T02:26:30',
      recordsProcessed: 8920,
      duration: 90,
      triggeredBy: 'Automated Schedule',
      details: 'Employee performance metrics, attendance records, and workforce analytics data synchronized',
    },
    {
      id: 4,
      system: 'Jira Project Management',
      status: 'warning',
      startTime: '2026-02-10T01:45:00',
      endTime: '2026-02-10T01:48:20',
      recordsProcessed: 5432,
      duration: 200,
      triggeredBy: 'Manual Trigger',
      details: 'Partial sync completed with 12 records skipped due to data validation errors in sprint velocity calculations',
    },
    {
      id: 5,
      system: 'Power BI Analytics',
      status: 'success',
      startTime: '2026-02-10T02:32:00',
      endTime: '2026-02-10T02:33:10',
      recordsProcessed: 23456,
      duration: 70,
      triggeredBy: 'Automated Schedule',
      details: 'Analytics dashboards and visualization datasets refreshed with latest business intelligence metrics',
    },
    {
      id: 6,
      system: 'Azure Data Lake',
      status: 'failed',
      startTime: '2026-02-09T18:20:00',
      endTime: '2026-02-09T18:20:45',
      recordsProcessed: 0,
      duration: 45,
      triggeredBy: 'Automated Schedule',
      details: 'Connection timeout error - Unable to establish secure connection to data lake storage endpoint',
    },
    {
      id: 7,
      system: 'SAP ERP',
      status: 'success',
      startTime: '2026-02-10T00:30:00',
      endTime: '2026-02-10T00:32:20',
      recordsProcessed: 44890,
      duration: 140,
      triggeredBy: 'Automated Schedule',
      details: 'Nightly batch processing completed for financial transactions and inventory management data',
    },
    {
      id: 8,
      system: 'Salesforce CRM',
      status: 'success',
      startTime: '2026-02-09T22:28:00',
      endTime: '2026-02-09T22:29:35',
      recordsProcessed: 11980,
      duration: 95,
      triggeredBy: 'Automated Schedule',
      details: 'Evening sync of customer interactions, lead conversions, and opportunity stage updates',
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-success bg-success/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      case 'failed':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'CheckCircle2';
      case 'warning':
        return 'AlertTriangle';
      case 'failed':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const filteredHistories = refreshHistories?.filter((history) => {
      const matchesSearch = history?.system?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesFilter =
        filterStatus === 'all' || history?.status === filterStatus;
      return matchesSearch && matchesFilter;
    })?.sort((a, b) => {
      const dateA = new Date(a.startTime);
      const dateB = new Date(b.startTime);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <div className="relative flex-1">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search by system name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="failed">Failed</option>
          </select>
          <Button
            variant="outline"
            iconName={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          >
            Sort
          </Button>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        {filteredHistories?.map((history) => (
          <div
            key={history?.id}
            className="bg-card border border-border rounded-md p-4 md:p-6 hover:shadow-md transition-smooth"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                    {history?.system}
                  </h3>
                  <div
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption ${getStatusColor(
                      history?.status
                    )}`}
                  >
                    <Icon name={getStatusIcon(history?.status)} size={12} />
                    <span className="capitalize">{history?.status}</span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-3 md:mb-4 line-clamp-2">
                  {history?.details}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      Start Time
                    </p>
                    <p className="text-sm font-medium text-foreground font-data">
                      {formatTimestamp(history?.startTime)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      End Time
                    </p>
                    <p className="text-sm font-medium text-foreground font-data">
                      {formatTimestamp(history?.endTime)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      Duration
                    </p>
                    <p className="text-sm font-medium text-foreground font-data">
                      {formatDuration(history?.duration)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      Records Processed
                    </p>
                    <p className="text-sm font-medium text-foreground font-data">
                      {history?.recordsProcessed?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex lg:flex-col gap-2">
                <Button variant="outline" size="sm" iconName="Eye">
                  Details
                </Button>
                <Button variant="ghost" size="sm" iconName="Download">
                  Export
                </Button>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground font-caption">
                Triggered by: <span className="text-foreground font-medium">{history?.triggeredBy}</span>
              </p>
            </div>
          </div>
        ))}
      </div>
      {filteredHistories?.length === 0 && (
        <div className="text-center py-12 md:py-16">
          <Icon
            name="History"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <p className="text-muted-foreground">No refresh histories found</p>
        </div>
      )}
    </div>
  );
};

export default DataRefreshHistories;